import { type Dtos, type SfaaCodeId, SuretyType } from "@inrev/common";
import { useMemo, useState } from "react";
import type { FieldError } from "react-hook-form";
import { SfaaCodeDirectory } from "../../features/sfaa-code/SfaaCodeDirectory";
import {
	EMPTY_SFAA_CODES,
	equivalentSfaaCode,
	isSfaaCodeForOtherOwner,
	isSfaaContractObligee,
	isSfaaFieldLocked,
	matchesSfaaObligee,
	mergeSfaaCatalog,
	type SfaaContractObligee,
	uniqueSfaaCodesById,
} from "../../features/sfaa-code/sfaaCodeFilters";
import {
	SearchableDropdownInput,
	type SearchableDropdownInputFooterAction,
	type SearchableDropdownInputOption,
} from "./SearchableDropdownInput";
import { SfaaCodeOwnerTypeFilter } from "./SfaaCodeOwnerTypeFilter";

type SfaaCode = Dtos.SfaaCode.Get.Response;

export type SfaaCodeSelectInputProps = {
	sfaaCodes?: Dtos.SfaaCode.List.Response;
	recentlyUsed?: SfaaCode[];
	suretyType: SuretyType;
	initialOwnerType?: SfaaContractObligee;
	isLoading?: boolean;
	value?: SfaaCodeId;
	onChange?: (value: SfaaCodeId | undefined) => void;
	disableDirectory?: boolean;
	disabled?: boolean;
	readonly?: boolean;
	compact?: boolean;
	error?: FieldError;
	errorMessage?: string;
	invalid?: boolean;
	placeholder?: string;
	id?: string;
	className?: string;
	"aria-labelledby"?: string;
};

const RECENT_LIMIT = 3;
const RECENT_GROUP = "Recently used";
const ALL_GROUP = "All";
const SEARCH_PLACEHOLDER = "Code or keyword(s) (Type, Sub-type, Class, etc.)";
const OPTION_CLASS = "mx-1.5 w-auto items-start rounded-md py-2 pl-2 pr-2";
const LIST_CLASS = "pt-1.5";
const POPOVER_HEIGHT_PX = 480;

const contractOwnerOf = (codes: SfaaCode[], value?: SfaaCodeId) => {
	const selected = codes.find((code) => code.id === value);
	return selected && isSfaaContractObligee(selected.obligeeType) ? selected.obligeeType : undefined;
};

const toOption = (code: SfaaCode, group?: string): SearchableDropdownInputOption => ({
	value: code.id,
	label: code.label,
	badge: code.code,
	subtitle: code.subClass ? `${code.class} > ${code.subClass}` : code.class,
	note: code.obligeeType ?? "All owners",
	group,
	searchValues: [code.code, code.label, code.type, code.subType, code.class, code.subClass],
});

const buildOptions = (recentCodes: SfaaCode[], allCodes: SfaaCode[]) => {
	if (recentCodes.length === 0) return allCodes.map((code) => toOption(code));

	const recentIds = new Set(recentCodes.map((code) => code.id));
	return [
		...recentCodes.map((code) => toOption(code, RECENT_GROUP)),
		...allCodes.filter((code) => !recentIds.has(code.id)).map((code) => toOption(code, ALL_GROUP)),
	];
};

export const SfaaCodeSelectInput = ({
	sfaaCodes,
	recentlyUsed = EMPTY_SFAA_CODES,
	suretyType,
	initialOwnerType = "Private",
	isLoading = false,
	value,
	onChange,
	disableDirectory = false,
	disabled,
	readonly,
	compact,
	error,
	errorMessage,
	invalid,
	placeholder = "Select SFAA code",
	id,
	className,
	"aria-labelledby": ariaLabelledBy,
}: SfaaCodeSelectInputProps) => {
	const catalogRows = useMemo(
		() => mergeSfaaCatalog(sfaaCodes, recentlyUsed),
		[sfaaCodes, recentlyUsed],
	);
	const attachedOwner = contractOwnerOf(catalogRows, value);
	const [ownerType, setOwnerType] = useState<SfaaContractObligee>(
		attachedOwner ?? initialOwnerType,
	);
	const [ownerSyncedFor, setOwnerSyncedFor] = useState<SfaaCodeId | undefined>(
		attachedOwner ? value : undefined,
	);
	const fieldLocked = isSfaaFieldLocked(disabled, readonly, isLoading);
	const [directoryOwner, setDirectoryOwner] = useState<SfaaContractObligee | null>(null);
	if ((disableDirectory || fieldLocked) && directoryOwner !== null) setDirectoryOwner(null);

	if (!value) {
		if (ownerSyncedFor !== undefined) setOwnerSyncedFor(undefined);
	} else if (attachedOwner && ownerSyncedFor !== value) {
		setOwnerSyncedFor(value);
		setOwnerType(attachedOwner);
	}

	const selectOwnerType = (nextOwnerType: SfaaContractObligee) => {
		if (nextOwnerType === ownerType) return;
		setOwnerType(nextOwnerType);
		const selected = catalogRows.find((code) => code.id === value);
		if (selected && isSfaaCodeForOtherOwner(selected.obligeeType, nextOwnerType)) {
			onChange?.(equivalentSfaaCode(catalogRows, selected, nextOwnerType)?.id);
		}
	};

	const recentCodes = useMemo(
		() => uniqueSfaaCodesById(recentlyUsed).slice(0, RECENT_LIMIT),
		[recentlyUsed],
	);

	const allCodes = useMemo(
		() => catalogRows.filter((code) => matchesSfaaObligee(code.obligeeType, suretyType, ownerType)),
		[catalogRows, suretyType, ownerType],
	);

	const options = useMemo(() => buildOptions(recentCodes, allCodes), [recentCodes, allCodes]);

	const ownerTypeFilter =
		suretyType === SuretyType.contract ? (
			<SfaaCodeOwnerTypeFilter value={ownerType} onChange={selectOwnerType} />
		) : undefined;

	const closeDirectory = () => setDirectoryOwner(null);
	const attachDirectory = (next: SfaaCodeId) => {
		onChange?.(next);
		closeDirectory();
	};
	const browseDirectory: SearchableDropdownInputFooterAction | undefined =
		fieldLocked || disableDirectory
			? undefined
			: {
					label: "Browse full directory →",
					icon: false,
					onSelect: () => setDirectoryOwner(ownerType),
				};

	return (
		<div className={className} data-testid="sfaa-code-select">
			<SearchableDropdownInput
				id={id}
				aria-labelledby={ariaLabelledBy}
				value={value}
				onChange={(next) => onChange?.(catalogRows.find((code) => code.id === next)?.id)}
				options={options}
				optionClassName={OPTION_CLASS}
				noteClassName="mt-1.5"
				listClassName={recentCodes.length === 0 ? LIST_CLASS : undefined}
				searchPlacement="popover"
				searchPlaceholder={SEARCH_PLACEHOLDER}
				placeholder={isLoading ? "Loading…" : placeholder}
				listHeader={ownerTypeFilter}
				popoverHeight={POPOVER_HEIGHT_PX}
				footerHint="↑↓ to move · Enter to select"
				footerAction={browseDirectory}
				emptyMessage="No matching class"
				disabled={disabled || isLoading}
				readonly={readonly}
				compact={compact}
				error={error}
				errorMessage={errorMessage}
				invalid={invalid}
			/>
			{directoryOwner !== null && (
				<SfaaCodeDirectory
					sfaaCodes={catalogRows}
					suretyType={suretyType}
					initialOwnerType={directoryOwner}
					value={value}
					onAttach={attachDirectory}
					onClose={closeDirectory}
				/>
			)}
		</div>
	);
};
